/**
 * Codec — bigint ↔ string conversion at the InstantDB boundary.
 *
 * InstantDB's i.string() / i.number() / i.boolean() / i.json() field types
 * don't include BigInt. BizOP uses BigInt heavily for money (paisa), FX rates
 * (E8 scaled), and percentages (E4 scaled). We encode every BigInt as a plain
 * base-10 numeric string on the wire and revive it back to BigInt on read.
 *
 * Each entity declares its BigInt fields here. The hook layer calls
 * `encodeEntity(entityName, obj)` before writing and `decodeEntity(entityName, obj)`
 * after reading. This keeps all 54 call sites untouched — they continue to
 * receive `bigint` values exactly as today.
 *
 * Note: a few BigInt fields are optional (nullable). Encoding null is a no-op
 * (stays null) and decoding null/undefined returns null/undefined.
 */

import type { DbEntityName } from "./entityNames";

/**
 * Map of entity name → array of field names that should be BigInt-encoded.
 * Fields not listed here pass through unchanged.
 *
 * IMPORTANT: keep this in sync with instant.schema.ts. Any field declared as
 * a bigint in a Mock* TypeScript type AND mapped to i.string() in the schema
 * must appear here.
 */
export const BIGINT_FIELDS: Record<DbEntityName, readonly string[]> = {
  // ── Sales ──────────────────────────────────────────────────────────────
  dealers: [
    "creditLimitCents",
    "creditUsedCents",
    "openingBalanceBaseCents",
  ],
  dealerTiers: ["pointsMultiplierE4"],
  salesOrders: [
    "fxRateToBaseE8",
    "amountCents",
    "discountInBaseCents",
    "taxInBaseCents",
    "totalInBaseCents",
    "pointsEarned",
    "pointsRedeemed",
  ],
  salesOrderLines: ["unitPriceCents", "lineTotalInBaseCents"],
  salesOrderStatusHistory: [],
  dealerOffers: ["unitPriceCents"],

  // ── Invoicing ──────────────────────────────────────────────────────────
  invoices: [
    "fxRateToBaseE8",
    "subtotalAmountCents",
    "discountAmountCents",
    "taxAmountCents",
    "shippingAmountCents",
    "totalAmountCents",
    "totalAmountInBaseCents",
    "paidAmountCents",
    "paidAmountInBaseCents",
    "balanceDueCents",
    "generalDiscountCents",
    "generalDiscountValue",
    "vatAmountCents",
  ],
  invoiceLines: [
    "discountPercentE4",
    "discountValue",
    "unitPriceCents",
    "netUnitPriceCents",
    "taxRatePercentE4",
    "lineSubtotalCents",
    "lineDiscountCents",
    "lineTaxCents",
    "lineTotalCents",
    "fxRateToBaseE8",
    "lineTotalInBaseCents",
  ],
  payments: ["amountCents", "fxRateToBaseE8", "amountInBaseCents"],
  creditNotes: [
    "amountCents",
    "fxRateToBaseE8",
    "amountInBaseCents",
    "remainingAmountCents",
    "remainingAmountInBaseCents",
  ],

  // ── Catalog ────────────────────────────────────────────────────────────
  brands: [],
  categories: [],
  units: [],
  taxRates: ["ratePercentE4"],
  products: ["standardCostCents"],
  productVariants: [],
  boms: [],
  bomLines: ["wastageAllowanceE4"],

  // ── Inventory ──────────────────────────────────────────────────────────
  branches: [],
  warehouses: [],
  stockMovements: ["unitCostBaseCents", "valueDeltaBaseCents"],
  stockReservations: [],

  // ── Workforce ──────────────────────────────────────────────────────────
  employees: ["baseSalaryCents"],
  attendance: [],
  leaves: [],
  employeePayments: [
    "baseSalaryCents",
    "overtimeCents",
    "bonusCents",
    "deductionsCents",
    "netCents",
    "fxRateToBaseE8",
    "netInBaseCents",
  ],

  // ── Finance ────────────────────────────────────────────────────────────
  loans: [
    "principalCents",
    "outstandingPrincipalCents",
    "fxRateToBaseE8",
    "amountInBaseCents",
    "outstandingPrincipalInBaseCents",
    "interestRatePercentE4",
  ],
  loanRepayments: ["principalCents", "interestCents", "totalCents"],
  expenses: ["amountCents", "amountInBaseCents", "fxRateToBaseE8"],
  fxRates: ["rateE8"],

  // ── GL ─────────────────────────────────────────────────────────────────
  accounts: ["currentBalanceInBaseCents"],
  journalEntries: [],
  journalLines: [
    "debitInBaseCents",
    "creditInBaseCents",
    "originalAmountCents",
    "fxRateToBaseE8",
  ],

  // ── Payables ───────────────────────────────────────────────────────────
  supplierPayments: ["amountCents", "amountInBaseCents"],
  supplierInvoices: ["amountCents", "amountInBaseCents"],
  suppliers: [],

  // ── Pricing ────────────────────────────────────────────────────────────
  priceLists: [],
  priceListItems: ["unitPriceCents"],
  dealerPriceListAssignments: [],

  // ── Dealer areas ───────────────────────────────────────────────────────
  dealerAreas: [],

  // ── Production ─────────────────────────────────────────────────────────
  workOrders: [],
  workOrderConsumptions: ["costPerUnitInBaseCents"],
  workOrderProductions: ["landedCostInBaseCents"],

  // ── Imports ────────────────────────────────────────────────────────────
  lettersOfCredit: [
    "amountCents",
    "fxRateToBaseE8",
    "amountInBaseCents",
    "drawnAmountCents",
    "drawnAmountInBaseCents",
  ],
  purchaseOrders: [
    "fxRateToBaseE8",
    "subtotalAmountCents",
    "taxAmountCents",
    "shippingAmountCents",
    "totalAmountCents",
    "totalAmountInBaseCents",
  ],
  shipments: ["landedCostBaseCents"],

  // ── Settings singletons ────────────────────────────────────────────────
  companyProfile: ["defaultVatRatePercentE4"],

  // ── Notifications / attachments / audit / roles ───────────────────────
  notifications: [],
  attachments: [],
  auditEntries: [],
  roles: [],
  officeUsers: [],

  // ── Sales Operations ───────────────────────────────────────────────────
  returns: ["totalAmountCents"],
  deliveries: [],
  stockTransfers: ["totalValueCents"],
};

/**
 * Fields that contain BigInt-aware JSON strings (stored as i.string() in schema).
 * The codec encodes/decodes these using jsonEncodeWithBigint / jsonDecodeWithBigint.
 */
export const JSON_BIGINT_FIELDS: Partial<Record<DbEntityName, readonly string[]>> = {
  returns: ["items"],
  stockTransfers: ["items"],
};

/**
 * Compact marker for BigInt values serialised inside JSON blobs.
 * Short and URL-safe so it survives round-trips through any transport layer.
 */
const _BJ = "\x00B:";  // BigInt-JSON marker

/** Stringify a value that may contain BigInts (e.g. an items array). */
function jsonEncodeWithBigint(v: unknown): string {
  return JSON.stringify(v, (_, val) =>
    typeof val === "bigint" ? `${_BJ}${val}` : val
  );
}

/** Parse a JSON string that was encoded with jsonEncodeWithBigint. */
function jsonDecodeWithBigint(s: string): unknown {
  if (!s) return null;
  try {
    return JSON.parse(s, (_, val) => {
      if (typeof val === "string" && val.startsWith(_BJ)) {
        return BigInt(val.slice(_BJ.length));
      }
      return val;
    });
  } catch {
    return null;
  }
}

/**
 * Encode a single value as the database scalar.
 * - bigint → base-10 string
 * - null/undefined → passed through (the schema makes the field optional)
 * - everything else → identity
 */
function encodeValue(value: unknown): unknown {
  if (typeof value === "bigint") return value.toString(10);
  return value;
}

/**
 * Decode a scalar back to its application-level type.
 * - string with all-digits (optional minus) AND the field is in the bigint list → BigInt
 * - everything else → identity
 *
 * We pass `isBigint` rather than inferring so we never accidentally turn a
 * legitimate numeric string (e.g. a tax ID) into a BigInt.
 */
function decodeValue(value: unknown, isBigint: boolean): unknown {
  if (!isBigint) return value;
  if (value == null) return value;
  if (typeof value === "bigint") return value;
  if (typeof value === "string") {
    try {
      return BigInt(value);
    } catch {
      return value;
    }
  }
  if (typeof value === "number") {
    // Should never happen — write path always sends strings — but be defensive
    return BigInt(Math.trunc(value));
  }
  return value;
}

/**
 * Encode an entity for write to InstantDB.
 * Returns a shallow-copied object with bigint fields converted to strings.
 *
 * The `id` (InstantDB UUID) is NOT generated here — callers pass it explicitly
 * to `db.tx[entity][id].update(...)`. The `_id` field (legacy string id) is
 * preserved verbatim.
 */
export function encodeEntity<T extends Record<string, unknown>>(
  entityName: DbEntityName,
  entity: T,
): Record<string, unknown> {
  const bigintFields = BIGINT_FIELDS[entityName] ?? [];
  const bigintSet = new Set(bigintFields);
  const jsonBigintFields = new Set(JSON_BIGINT_FIELDS[entityName] ?? []);
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(entity)) {
    const v = entity[k];
    if (bigintSet.has(k)) {
      out[k] = encodeValue(v);
    } else if (jsonBigintFields.has(k)) {
      out[k] = jsonEncodeWithBigint(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Decode an entity received from InstantDB back to the application type
 * shape with native bigints.
 *
 * If `entityName` has no BigInt fields, this is a near-zero-cost pass-through.
 */
export function decodeEntity<T = Record<string, unknown>>(
  entityName: DbEntityName,
  entity: Record<string, unknown>,
): T {
  const bigintFields = BIGINT_FIELDS[entityName] ?? [];
  const jsonBigintFields = new Set(JSON_BIGINT_FIELDS[entityName] ?? []);
  if (bigintFields.length === 0 && jsonBigintFields.size === 0) return entity as T;
  const bigintSet = new Set(bigintFields);
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(entity)) {
    if (bigintSet.has(k)) {
      out[k] = decodeValue(entity[k], true);
    } else if (jsonBigintFields.has(k)) {
      out[k] = jsonDecodeWithBigint(entity[k] as string);
    } else {
      out[k] = entity[k];
    }
  }
  return out as T;
}

/** Batch decoder — convenient at query-result boundary. */
export function decodeEntities<T>(
  entityName: DbEntityName,
  entities: ReadonlyArray<Record<string, unknown>>,
): T[] {
  return entities.map((e) => decodeEntity<T>(entityName, e));
}
